import {
  createContext,
  useContext,
  createElement,
  type PropsWithChildren,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth0Login } from "./useAuth0Login";
import { orpc } from "~/lib/orpc";
import { setAuthTokenProvider, type apiClient } from "~/lib/orpc-client";

type AuthenticatedUser = Awaited<ReturnType<typeof apiClient.users.getMe>>;

type AuthenticatedUserContextValue = {
  user: AuthenticatedUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  accountType: AuthenticatedUser["accountType"] | null;
  organizationId: string | null;
  organizationSlug: string | null;
  organizationType: string | null;
  organizationName: string | null;
  capabilities: AuthenticatedUser["capabilities"] | null;
  routing: AuthenticatedUser["routing"] | null;
  isPersonalAccount: boolean;
  isPublisherAccount: boolean;
  isUniversityAccount: boolean;
};

const AuthenticatedUserContext =
  createContext<AuthenticatedUserContextValue | null>(null);

export function AuthenticatedUserProvider({ children }: PropsWithChildren) {
  const { getToken, isAuthenticated, logout } = useAuth0Login();

  // Sync the token getter so all services can authenticate automatically
  setAuthTokenProvider(getToken);

  const { data, isLoading } = useQuery({
    ...orpc.users.getMe.queryOptions(),
    enabled: isAuthenticated,
    retry: (failureCount, error) => {
      if ((error as { status?: number })?.status === 401) return false;
      return failureCount < 2;
    },
    throwOnError: (error: any) => {
      if ((error)?.status === 401) {
        logout();
      }
      return false;
    },
  });

  const value: AuthenticatedUserContextValue = {
    user: data ?? null,
    isLoading,
    isAuthenticated,
    accountType: data?.accountType ?? null,
    organizationId: data?.organizationId ?? null,
    organizationSlug: data?.organizationSlug ?? null,
    organizationType: data?.organizationType ?? null,
    organizationName: data?.organizationName ?? null,
    capabilities: data?.capabilities ?? null,
    routing: data?.routing ?? null,
    isPersonalAccount: data?.accountType === "personal",
    isPublisherAccount: data?.accountType === "publisher",
    isUniversityAccount: data?.accountType === "university",
  };

  return createElement(AuthenticatedUserContext.Provider, { value }, children);
}

export const useAuthenticatedUser = (): AuthenticatedUserContextValue => {
  const context = useContext(AuthenticatedUserContext);

  if (!context) {
    throw new Error(
      "useAuthenticatedUser must be used within an AuthenticatedUserProvider"
    );
  }

  return context;
};
